import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { expeditionService } from "../services/expedition";
import { productService } from "../services/product";
import { getUnitWeight } from "../utils/packUtils";
import { toast } from "react-hot-toast"; // Importar toast

const EditExpedition = () => {
  const { id } = useParams();
  const [expeditionData, setExpeditionData] = useState({
    order_id: "",
    expedition_number: "",
    shipping_company: "",
    invoice_number: "",
    is_packed: false,
    status: "",
    notes: "", // Novo campo
    expedition_items: [],
  });

  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedQuantity, setSelectedQuantity] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [expedition, productsData] = await Promise.all([
          expeditionService.getExpeditionById(id),
          productService.getProducts(),
        ]);

        setExpeditionData({
          ...expedition,
          status: expedition.status || "pending",
          notes: expedition.notes || "", // Inicializar novo campo
          expedition_items: expedition.expedition_items.map((item) => {
            const productWeightPerUnit = getUnitWeight(item.products);
            // Se o peso por unidade for desconhecido, ou o item pesar menos
            // que uma unidade, mostrar sempre pelo menos 1 unidade.
            const calculatedUnits = productWeightPerUnit
              ? Math.max(1, item.expedited_quantity / productWeightPerUnit)
              : 1;

            return {
              ...item,
              product_name: item.products.name,
              expedited_weight_kg: item.expedited_quantity, // Peso total do item
              expedited_units_count: calculatedUnits, // Unidades calculadas
              products: item.products, // Manter o objeto products para cálculos futuros
            };
          }),
        });
        setProducts(Array.isArray(productsData) ? productsData : []);
      } catch (err) {
        console.error("Erro ao carregar dados para edição:", err);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setExpeditionData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const addExpeditionItem = () => {
    setError("");
    if (!selectedProduct || selectedQuantity <= 0) {
      setError("Selecione um produto e insira uma quantidade válida");
      return;
    }

    const productToAdd = products.find((p) => p.id === selectedProduct);
    if (!productToAdd) {
      setError("Produto selecionado não encontrado.");
      return;
    }

    const productWeightPerUnit = getUnitWeight(productToAdd);

    // Peso por unidade só é necessário para calcular a divisão da expedição —
    // a sua ausência não deve impedir adicionar o item aqui.
    const expeditedWeightKg = productWeightPerUnit
      ? selectedQuantity * productWeightPerUnit
      : 0;

    setExpeditionData((prev) => ({
      ...prev,
      expedition_items: [
        ...prev.expedition_items,
        {
          product_id: selectedProduct,
          product_name: productToAdd.name,
          expedited_units_count: selectedQuantity, // Unidades
          expedited_weight_kg: expeditedWeightKg, // Peso total
          products: productToAdd, // Manter o objeto products para cálculos futuros
        },
      ],
    }));

    setSelectedProduct("");
    setSelectedQuantity(0);
  };

  const removeExpeditionItem = (index) => {
    setExpeditionData((prev) => ({
      ...prev,
      expedition_items: prev.expedition_items.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (expeditionData.expedition_items.length === 0) {
      setError("Adicione pelo menos um item à expedição");
      toast.error("Adicione pelo menos um item à expedição");
      return;
    }

    try {
      const expeditionDataToSend = {
        order_id: expeditionData.order_id,
        shipping_company: expeditionData.shipping_company,
        invoice_number: expeditionData.invoice_number,
        status: expeditionData.status,
        is_packed: expeditionData.is_packed,
        notes: expeditionData.notes, // Enviar novo campo
        expedition_items: expeditionData.expedition_items.map((item) => ({
          // Itens já existentes têm item.id (vindo da expedição carregada);
          // itens novos (adicionados nesta sessão) não têm — o backend trata
          // a ausência de item_id como "criar linha nova".
          item_id: item.id,
          product_id: item.product_id,
          expedited_units: item.expedited_weight_kg, // Enviar o peso total para o backend
        })),
      };

      await expeditionService.updateExpedition(id, expeditionDataToSend);
      toast.success("Expedição atualizada com sucesso!");
      navigate(-1);
    } catch (err) {
      setError(err.message || "Erro ao atualizar expedição");
      toast.error(`Erro ao atualizar expedição: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <div className="container-fluid p-4 sm:p-6 lg:p-8">Carregando...</div>
    );
  }

  return (
    <div className="container-fluid p-4 sm:p-6 lg:p-8">
      <h1 className="text-3xl font-bold text-neutral-800 mb-8">
        Editar Expedição #{expeditionData.expedition_number}
      </h1>

      <form
        onSubmit={handleSubmit}
        className="bg-white shadow-md rounded-lg p-8"
      >
        {error && (
          <div
            className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6"
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm text-neutral-700 mb-2">
              Ordem de Encomenda
            </label>
            <input
              type="text"
              value={
                expeditionData.orders
                  ? `${expeditionData.orders.order_number} - ${expeditionData.orders.client_name}`
                  : ""
              }
              disabled
              className="w-full px-3 py-2 border border-neutral-300 rounded-md bg-neutral-100 text-neutral-600"
            />
          </div>

          <div>
            <label className="block text-sm text-neutral-700 mb-2">
              Transportadora
            </label>
            <input
              type="text"
              name="shipping_company"
              value={expeditionData.shipping_company}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400"
            />
          </div>

          <div>
            <label className="block text-sm text-neutral-700 mb-2">
              Número da Fatura
            </label>
            <input
              type="text"
              name="invoice_number"
              value={expeditionData.invoice_number}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400"
            />
          </div>

          <div>
            <label className="block text-sm text-neutral-700 mb-2">
              Status
            </label>
            <select
              name="status"
              value={expeditionData.status}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400"
            >
              <option value="pending">Pendente</option>
              <option value="completed">Concluída</option>
              <option value="divided">Dividida</option>
              <option value="canceled">Cancelada</option>
            </select>
          </div>


          <div className="md:col-span-2">
            <label className="block text-sm text-neutral-700 mb-2">
              Observações
            </label>
            <textarea
              name="notes"
              value={expeditionData.notes}
              onChange={handleInputChange}
              rows="3"
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400"
            ></textarea>
          </div>

          <div className="md:col-span-2">
            <h2 className="text-xl font-semibold text-neutral-800 mb-4">
              Itens da Expedição
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm text-neutral-700 mb-2">
                  Produto
                </label>
                <select
                  value={selectedProduct}
                  onChange={(e) => setSelectedProduct(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md"
                >
                  <option value="">Selecione um produto</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-neutral-700 mb-2">
                  Quantidade (unidades)
                </label>
                <input
                  type="number"
                  value={selectedQuantity}
                  onChange={(e) =>
                    setSelectedQuantity(Number(e.target.value))
                  }
                  min="0"
                  step="1"
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md"
                />
              </div>

              <div className="flex items-end">
                <button
                  type="button"
                  onClick={addExpeditionItem}
                  className="w-full md:w-auto bg-neutral-900 text-white px-4 py-2 rounded-md hover:bg-neutral-800"
                >
                  Adicionar Item
                </button>
              </div>
            </div>

            {expeditionData.expedition_items.length > 0 && (
              <div className="bg-neutral-100 rounded-md p-4">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-full">
                    <thead>
                      <tr className="text-left">
                        <th className="pb-2 text-sm">Produto</th>
                        <th className="pb-2 text-sm">Unidades</th>
                        <th className="pb-2 text-sm">Peso Total (kg)</th>
                        <th className="pb-2 text-right text-sm">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expeditionData.expedition_items.map((item, index) => (
                        <tr key={index} className="border-t border-neutral-200">
                          <td className="py-2 text-sm">{item.product_name}</td>
                          <td className="py-2 text-sm">
                            {(item.expedited_units_count || 0).toFixed(0)}
                          </td>
                          <td className="py-2 text-sm">
                            {(item.expedited_weight_kg || 0).toFixed(2)}
                          </td>
                          <td className="py-2 text-right">
                            <button
                              type="button"
                              onClick={() => removeExpeditionItem(index)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              Remover
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Botões de ação responsivos */}
        <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4 sm:justify-end">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="w-full sm:w-auto bg-neutral-100 text-neutral-900 px-4 py-2 rounded-md hover:bg-neutral-200"
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="w-full sm:w-auto bg-neutral-900 text-white px-4 py-2 rounded-md hover:bg-neutral-800"
          >
            Guardar Alterações
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditExpedition;
